import type { SclNode } from "./jsonContracts";

const decoder = new TextDecoder("utf-8");

// ── Detección de formato ──────────────────────────────────────────────────────
/** Los primeros 4 bytes de un xlsx/xls son la firma ZIP (PK\x03\x04). */
function isXlsx(bytes: Uint8Array): boolean {
  return bytes[0]===0x50 && bytes[1]===0x4b && bytes[2]===0x03 && bytes[3]===0x04;
}

// ── Parser xlsx con SheetJS ───────────────────────────────────────────────────
async function parseXlsxBytes(
  bytes: Uint8Array,
): Promise<Array<{signal: string; mmsNode?: string}>> {
  const XLSX = await import("xlsx");
  const wb   = XLSX.read(bytes, { type: "array" });
  const ws   = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: "" });

  const result: Array<{signal: string; mmsNode?: string}> = [];
  let start = 0;
  if (rows.length) {
    const first = String(rows[0][0] ?? "").toLowerCase();
    if (/senal|señal|signal|nombre|name|tag|descripcion/.test(first)) start = 1;
  }
  for (let i = start; i < rows.length; i++) {
    const row    = rows[i];
    const signal = String(row[0] ?? "").trim();
    if (!signal) continue;
    const col1   = String(row[1] ?? "").trim();
    const mmsNode = col1 && /[./]/.test(col1) ? col1 : undefined;
    result.push({ signal, mmsNode });
  }
  return result;
}

// ── Parser CSV / TXT ──────────────────────────────────────────────────────────
function parseCsvBytes(bytes: Uint8Array): Array<{signal: string; mmsNode?: string}> {
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    // Fallback para archivos guardados con encoding Windows
    text = new TextDecoder("windows-1252").decode(bytes);
  }
  const rows  = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  let start = 0;
  if (rows.length && /senal|señal|signal|nombre|name|tag/i.test(rows[0])) start = 1;
  const result: Array<{signal: string; mmsNode?: string}> = [];
  for (let i = start; i < rows.length; i++) {
    const cols   = rows[i].split(/[,;\t]/).map(c => c.trim().replace(/^"|"$/g, ""));
    const signal = cols[0];
    if (!signal) continue;
    const mmsNode = cols[1] && /[./]/.test(cols[1]) ? cols[1] : undefined;
    result.push({ signal, mmsNode });
  }
  return result;
}

// ── Función principal — detecta formato automáticamente ───────────────────────
export async function parseSignalList(
  bytes: Uint8Array,
  filename = "",
): Promise<Array<{signal: string; mmsNode?: string}>> {
  if (isXlsx(bytes)) return parseXlsxBytes(bytes);
  return parseCsvBytes(bytes);
}

// ── Parser SCL (sin cambios) ──────────────────────────────────────────────────
export function parseSclNodes(bytes: Uint8Array): SclNode[] {
  const xml   = decoder.decode(bytes);
  const nodes: SclNode[] = [];
  const seen  = new Set<string>();

  // Etapa 2 PROMPT UNIVERSAL v2: recorrer IED > LDevice > LN0 > DataSet > FCDA
  const iedRe = /<IED\b[^>]*\bname="([^"]+)"/g;
  let iedM: RegExpExecArray|null;

  while ((iedM = iedRe.exec(xml))) {
    const iedName  = iedM[1];
    const iedEnd   = xml.indexOf("</IED>", iedM.index);
    const iedBlock = iedEnd>0 ? xml.slice(iedM.index, iedEnd) : xml.slice(iedM.index);

    const ldRe = /<LDevice\b[^>]*\binst="([^"]+)"/g;
    let ldM: RegExpExecArray|null;

    while ((ldM = ldRe.exec(iedBlock))) {
      const ldInst   = ldM[1];
      const ldEnd    = iedBlock.indexOf("</LDevice>", ldM.index);
      const ldBlock  = ldEnd>0 ? iedBlock.slice(ldM.index, ldEnd) : iedBlock.slice(ldM.index);

      // Buscar DataSets (pueden estar en LN0 o directamente en LDevice)
      const dsRe = /<DataSet\b[^>]*\bname="([^"]+)"[^>]*>([\s\S]*?)<\/DataSet>/g;
      let dsM: RegExpExecArray|null;

      while ((dsM = dsRe.exec(ldBlock))) {
        const datasetName = dsM[1];
        const dsBody      = dsM[2];

        // Extraer cada FCDA del DataSet
        const fcdaRe = /<FCDA\b([^>]*?)\/>/g;
        let fcdaM: RegExpExecArray|null;

        while ((fcdaM = fcdaRe.exec(dsBody))) {
          const attrs  = fcdaM[1];
          const ga = (n: string) => new RegExp(`\\b${n}="([^"]*)"`, "").exec(attrs)?.[1] ?? "";
          const fLd  = ga("ldInst"), fPfx = ga("prefix"),
                fCls = ga("lnClass"), fIn  = ga("lnInst"),
                fDo  = ga("doName"),  fFc  = ga("fc");
          if (!fCls || !fDo) continue;
          const key = `${iedName}|${fLd}|${fPfx}|${fCls}|${fIn}|${fDo}`;
          if (!seen.has(key)) {
            seen.add(key);
            // El doName puede ser compuesto (A.phsA) — guardar ambos
          const fDoBase = fDo.split(".")[0]; // parte base: A
          nodes.push({
              iedName, ldInst:fLd, prefix:fPfx, lnClass:fCls,
              lnInst:fIn, doiName:fDo, doiBase:fDoBase, fc:fFc, dataset:datasetName,
              mmsPath:`${fLd}/${fPfx}${fCls}${fIn}.${fDo}`,
            } as SclNode);
          // Si el doName tiene DA (A.phsA ≠ A), añadir también la entrada con doName base
          if (fDoBase !== fDo) {
            const key2 = `${iedName}|${fLd}|${fPfx}|${fCls}|${fIn}|${fDoBase}`;
            if (!seen.has(key2)) {
              seen.add(key2);
              nodes.push({
                iedName, ldInst:fLd, prefix:fPfx, lnClass:fCls,
                lnInst:fIn, doiName:fDoBase, doiBase:fDoBase, fc:fFc, dataset:datasetName,
                mmsPath:`${fLd}/${fPfx}${fCls}${fIn}.${fDoBase}`,
              } as SclNode);
            }
          }
          }
        }
      }
    }
  }
  return nodes;
}
